import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Paper,
  Divider,
  Stack,
  Snackbar,
  Alert
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import ArticuloService from '../services/articulos';
import VariantCard from './FormArticulo/VariantCard';

const VariantsModal = ({ open, onClose, article, storeId, onSuccess }) => {
  const [typeDetails, setTypeDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (open && article) {
      // Inicializar con las variantes existentes o un array vacío
      // Asegurar que cada variante tenga un ID único
      const variantsWithIds = (article.type_detail || []).map((v, idx) => ({
        ...v,
        _id: v._id || `variant-${Date.now()}-${idx}-${Math.random()}`
      }));
      setTypeDetails(variantsWithIds);
    }
  }, [open, article]);

  const handleAddVariant = () => {
    setTypeDetails([
      ...typeDetails,
      { 
        color: '', 
        sizes: [], 
        images: [], 
        available: true,
        quantity: 0,
        _id: `variant-${Date.now()}-${typeDetails.length}-${Math.random()}`
      }
    ]);
  };

  const handleRemoveVariant = useCallback((index) => {
    const newTypeDetails = typeDetails.filter((_, i) => i !== index);
    setTypeDetails(newTypeDetails);
  }, [typeDetails]);

  const handleVariantChange = useCallback((index, field, value) => {
    const newTypeDetails = [...typeDetails];
    newTypeDetails[index] = {
      ...newTypeDetails[index],
      [field]: value
    };
    setTypeDetails(newTypeDetails);
  }, [typeDetails]);

  // Handler atómico para size_details + quantity (evita doble setState con closure stale)
  const handleSizeDetailsChange = useCallback((index, newSizeDetails, totalQty) => {
    const newTypeDetails = [...typeDetails];
    newTypeDetails[index] = {
      ...newTypeDetails[index],
      size_details: newSizeDetails,
      quantity: totalQty,
    };
    setTypeDetails(newTypeDetails);
  }, [typeDetails]);

  const handleAddSize = useCallback((index, size) => {
    if (!size.trim()) return;
    const newTypeDetails = [...typeDetails];
    const currentSizes = newTypeDetails[index].sizes || [];
    if (!currentSizes.includes(size.trim())) {
      newTypeDetails[index].sizes = [...currentSizes, size.trim()];
      // Agregar al size_details si no existe aún
      const currentSizeDetails = newTypeDetails[index].size_details || [];
      if (!currentSizeDetails.find(sd => sd.size === size.trim())) {
        newTypeDetails[index].size_details = [...currentSizeDetails, { size: size.trim(), quantity: 0, available: false }];
      }
      setTypeDetails(newTypeDetails);
    }
  }, [typeDetails]);

  const handleRemoveSize = useCallback((variantIndex, size) => {
    const newTypeDetails = [...typeDetails];
    newTypeDetails[variantIndex].sizes = newTypeDetails[variantIndex].sizes.filter(s => s !== size);
    // Sincronizar size_details
    if (newTypeDetails[variantIndex].size_details) {
      newTypeDetails[variantIndex].size_details = newTypeDetails[variantIndex].size_details.filter(sd => sd.size !== size);
      const totalQty = newTypeDetails[variantIndex].size_details.reduce((acc, sd) => acc + (sd.quantity || 0), 0);
      newTypeDetails[variantIndex].quantity = totalQty;
    }
    setTypeDetails(newTypeDetails);
  }, [typeDetails]);

  const handleAddImage = useCallback((index, imageUrl) => {
    console.log('VariantsModal handleAddImage llamado con index:', index, 'url:', imageUrl);
    if (!imageUrl.trim()) return;
    const newTypeDetails = [...typeDetails];
    console.log('Estado actual typeDetails:', typeDetails);
    const currentImages = newTypeDetails[index].images || [];
    if (!currentImages.includes(imageUrl.trim())) {
      newTypeDetails[index].images = [...currentImages, imageUrl.trim()];
      console.log('Agregando imagen al index', index, 'nuevo array de imágenes:', newTypeDetails[index].images);
      setTypeDetails(newTypeDetails);
    }
  }, [typeDetails]);

  const handleRemoveImage = useCallback((variantIndex, imageUrl) => {
    const newTypeDetails = [...typeDetails];
    newTypeDetails[variantIndex].images = newTypeDetails[variantIndex].images.filter(img => img !== imageUrl);
    setTypeDetails(newTypeDetails);
  }, [typeDetails]);

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validar que todas las variantes tengan color
      const invalidVariants = typeDetails.some(td => !td.color || !td.color.trim());
      if (invalidVariants) {
        setSnackbar({
          open: true,
          message: 'Todas las variantes deben tener un color especificado',
          severity: 'warning'
        });
        setLoading(false);
        return;
      }

      // Validar que tengan al menos una talla
      const noSizes = typeDetails.some(td => !td.sizes || td.sizes.length === 0);
      if (noSizes) {
        setSnackbar({
          open: true,
          message: 'Cada color debe tener al menos una talla',
          severity: 'warning'
        });
        setLoading(false);
        return;
      }

      // Validar que el total de cantidades no supere el stock del artículo
      const articleStock = article?.quantity ?? null;
      if (articleStock !== null) {
        const totalVariantsQty = typeDetails.reduce((acc, td) => acc + (td.quantity || 0), 0);
        if (totalVariantsQty > articleStock) {
          setSnackbar({
            open: true,
            message: `El stock total de variantes (${totalVariantsQty}) supera el stock del artículo (${articleStock}). Ajustá las cantidades.`,
            severity: 'error'
          });
          setLoading(false);
          return;
        }
      }

      // Limpiar solo campos temporales del front; size_details ahora es aceptado por el backend
      const cleanedTypeDetails = typeDetails.map(({ _id, ...rest }) => rest);

      const response = await ArticuloService.updateVariants(
        storeId,
        article._id || article.article_id,
        cleanedTypeDetails
      );

      setSnackbar({
        open: true,
        message: 'Variantes actualizadas correctamente',
        severity: 'success'
      });

      // Llamar al callback de éxito con el artículo actualizado
      if (onSuccess) {
        onSuccess(response);
      }

      // Cerrar el modal después de un breve delay
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      console.error('Error updating variants:', error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar variantes: ' + (error.message || 'Error desconocido'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTypeDetails([]);
    onClose();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!article) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: '#f2e8e4',
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box>
            <Typography variant="h6" component="div">
              Gestionar Variantes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {article.article?.name || article.name}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            {/* Indicador stock total vs variantes */}
            {article?.quantity != null && typeDetails.length > 0 && (() => {
              const used = typeDetails.reduce((acc, td) => acc + (td.quantity || 0), 0);
              const total = article.quantity;
              const over = used > total;
              return (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: over ? 'error.main' : used === total ? 'success.main' : 'text.secondary',
                    fontSize: '0.72rem',
                    mr: 0.5,
                  }}
                >
                  {`${used} / ${total} u.`}
                </Typography>
              );
            })()}
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {typeDetails.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: 'rgba(255,255,255,0.5)',
                  border: '1px dashed rgba(0,0,0,0.12)'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No hay variantes definidas. Haz clic en "Agregar Color" para comenzar.
                </Typography>
              </Paper>
            ) : (
              typeDetails.map((variant, index) => (
                <VariantCard
                  key={variant._id}
                  variant={variant}
                  index={index}
                  onRemove={handleRemoveVariant}
                  onChange={handleVariantChange}
                  onChangeSizeDetails={handleSizeDetailsChange}
                  onAddSize={handleAddSize}
                  onRemoveSize={handleRemoveSize}
                  onAddImage={handleAddImage}
                  onRemoveImage={handleRemoveImage}
                />
              ))
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddVariant}
            variant="outlined"
            size="small"
          >
            Agregar Color
          </Button>
          <Box flex={1} />
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || typeDetails.length === 0}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VariantsModal;
